import { motion } from "framer-motion";
import { SERVICES, PROCESS, IMAGES } from "@/data/site";
import { Reveal, Eyebrow } from "./Reveal";
import { Ga6Teaser } from "./Ga6Teaser";
import { BlueprintGrid } from "./Blueprint";
import { EASE } from "./motion";

export const Services = () => (
  <section id="services" className="relative bg-[#161614] text-[#FBFBF9] py-24 lg:py-36 overflow-hidden" data-testid="services-section">
    <BlueprintGrid dark />
    <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
      <div className="grid lg:grid-cols-12 gap-12">
        <Reveal className="lg:col-span-4">
          <Eyebrow className="mb-6 !text-stone-400">04 / Services</Eyebrow>
          <h2 className="font-serif font-light text-3xl sm:text-4xl lg:text-5xl tracking-tight">What we do</h2>
          <Reveal variant="rise" duration={1.1} className="mt-10 aspect-[16/9] lg:aspect-[4/5] overflow-hidden">
            <motion.img src={IMAGES.light} alt="Light across concrete and timber" className="w-full h-full object-cover opacity-90" initial={{ scale: 1.15 }} whileInView={{ scale: 1 }} viewport={{ once: true }} transition={{ duration: 1.6, ease: EASE }} />
          </Reveal>
        </Reveal>
        <div className="lg:col-span-8 relative">
          <motion.span aria-hidden="true" className="absolute top-0 left-0 right-0 h-px bg-stone-700 origin-left" initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }} viewport={{ once: true }} transition={{ duration: 1, ease: EASE }} />
          {SERVICES.map((s, i) => (
            <motion.div
              key={s.n}
              className="group relative grid grid-cols-12 gap-4 py-8 lg:py-10"
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.4 }}
              transition={{ staggerChildren: 0.08, delayChildren: i * 0.08 }}
              whileTap={{ x: 6 }}
              data-testid={`service-item-${s.n}`}
            >
              <motion.span aria-hidden="true" className="absolute bottom-0 left-0 right-0 h-px bg-stone-700 origin-left" variants={{ hidden: { scaleX: 0 }, show: { scaleX: 1 } }} transition={{ duration: 1, ease: EASE }} />
              <motion.span className="col-span-2 font-mono text-xs text-[#C29B38] pt-2" variants={{ hidden: { opacity: 0, x: -10 }, show: { opacity: 1, x: 0 } }} transition={{ duration: 0.6, ease: EASE }}>{s.n}</motion.span>
              <div className="col-span-10">
                <span className="block overflow-hidden">
                  <motion.h3 className="font-serif text-2xl lg:text-3xl transition-transform duration-500 group-hover:translate-x-2" variants={{ hidden: { y: "105%" }, show: { y: 0 } }} transition={{ duration: 0.9, ease: EASE }}>{s.title}</motion.h3>
                </span>
                <motion.p className="mt-3 text-stone-400 font-light leading-relaxed max-w-xl" variants={{ hidden: { opacity: 0, y: 8 }, show: { opacity: 1, y: 0 } }} transition={{ duration: 0.7, ease: EASE }}>{s.text}</motion.p>
              </div>
              <span aria-hidden="true" className="pointer-events-none absolute right-0 top-1/2 -translate-y-1/2 h-px w-0 bg-[#C29B38] transition-all duration-500 group-hover:w-16" />
            </motion.div>
          ))}
        </div>
      </div>

      <Reveal className="mt-24">
        <Eyebrow className="mb-10 !text-stone-400">Our process</Eyebrow>
        <div className="relative">
          <motion.span aria-hidden="true" className="absolute -top-px left-0 right-0 h-px bg-[#C29B38] origin-left z-10" initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }} viewport={{ once: true, amount: 0.3 }} transition={{ duration: 1.8, ease: EASE }} data-testid="process-line" />
          <motion.div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-stone-700" initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }} transition={{ staggerChildren: 0.2 }}>
            {PROCESS.map((p) => (
              <motion.div key={p.n} className="bg-[#161614] p-8 relative overflow-hidden" variants={{ hidden: { opacity: 0 }, show: { opacity: 1 } }} transition={{ duration: 0.6 }} whileHover={{ backgroundColor: "#1d1d1a" }} whileTap={{ backgroundColor: "#1d1d1a" }} data-testid={`process-step-${p.n}`}>
                <motion.span className="block font-serif italic text-4xl text-[#C29B38]" variants={{ hidden: { rotateX: -90, opacity: 0 }, show: { rotateX: 0, opacity: 1 } }} transition={{ duration: 0.8, ease: EASE }} style={{ transformOrigin: "bottom" }}>{p.n}</motion.span>
                <motion.h4 className="mt-6 text-lg font-medium" variants={{ hidden: { opacity: 0, y: 10 }, show: { opacity: 1, y: 0 } }} transition={{ duration: 0.6, ease: EASE }}>{p.title}</motion.h4>
                <motion.p className="mt-2 text-stone-400 text-sm font-light leading-relaxed" variants={{ hidden: { opacity: 0, y: 10 }, show: { opacity: 1, y: 0 } }} transition={{ duration: 0.6, ease: EASE }}>{p.text}</motion.p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </Reveal>
      <Ga6Teaser />
    </div>
  </section>
);
